<template>
    <div>
        <commodity-selector
            class="inline-block flex-col w-full sm:w-auto mr-1 xs:mr-0 print:w-1/4"
            v-model="modelCommodity"
            :original="initialSymbol"
            :groupings="groups">
        </commodity-selector>
        <div class="relative inline-block flex-col w-full sm:w-auto mr-2 xs:mr-0 print:w-1/4">
            <select class="form-control form-control-sm form-control-fixed bg-white mb-1"
                v-model="modelType"
                :class="{ 'loading': loading }"
                :disabled="loading"
                aria-label="Select Type"
            >
                <template v-for="option in types">
                    <option :class="{ 'bg-yellow-200': option.highlight }" :value="option.value">
                        {{ option.label }}
                    </option>
                </template>
            </select>
            <div v-if="loading" class="loading-spinner"></div>
        </div>
        <div class="relative inline-block flex-col w-full sm:w-auto mb-2 mr-2 xs:mr-0 print:w-1/4">
            <select class="form-control form-control-sm form-control-fixed bg-white mb-1"
                v-model="modelContract"
                :class="{ 'loading': loading }"
                :disabled="loading || (!contracts || contracts.length === 0)"
                aria-label="Select Contract"
            >
                <template v-for="option in contracts">
                    <option :class="{ 'bg-yellow-200': option.highlight }" :value="option.value">
                        {{ option.label }}
                    </option>
                </template>
            </select>
            <div v-if="loading" class="loading-spinner"></div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue';
const props = defineProps({
    initialSymbol: Object,
    groups: {
        type: Object,
        required: true
    },
    modelValue: {
        type: [String, Object],
        default: null, // You can set a default value if necessary
    },
});

// Specify the emitted events
const emit = defineEmits(['update:modelValue', 'error', 'update-description']);
// const emit = defineEmits();

const loading = ref(false);
const types = ref(null);
const contractData = ref(null);

const modelCommodity = ref(null);
const modelType = ref(null);
const modelContract = ref(null);

const contracts = computed(() => {
    if (modelType.value) {
        // find initial contract if set
        var selected = contractData.value[modelType.value].find(mContract => {
            return mContract.value.symbol === props.initialSymbol?.option && mContract.value.underlyingFuture === props.initialSymbol?.contract;
        });
        if (selected) {
            modelContract.value = selected?.value || contractData.value[modelType.value][0]?.value;
        } else {
            var findDefault = contractData.value[modelType.value].find(mContract => {
                return mContract.default;
            });
            modelContract.value = findDefault?.value|| contractData.value[modelType.value][0]?.value;
        }
        // modelContract.value = selected?.value || contractData.value[modelType.value][0]?.value;
        return contractData.value[modelType.value];
    }
});

const initialContractInRoot = () => {
    return props.initialSymbol &&
        props.initialSymbol.contract &&
        props.initialSymbol.contract.startsWith(modelCommodity.value.root);
};

const setDefaultContract = (data) => {
    let foundContract = false;
    const hasOption = props.initialSymbol.option;

    Object.keys(data).forEach((optionType) => {
        data[optionType] = data[optionType].map((mOption) => {
            if (hasOption) {
                if (mOption.value.symbol === props.initialSymbol.option) {
                    mOption.default = true;
                    foundContract = true;
                }
            } else {
                if (mOption.value.symbol === props.initialSymbol.contract) {
                    mOption.default = true;
                }
            }
            return mOption;
        });
    });

    if (hasOption && (!foundContract && initialContractInRoot())) {
        emit('error');
    }

    return data;
};

const setDefaultType = () => {
    const defaultTypes = Object.keys(contractData.value).map((key) => {
        const found = contractData.value[key].find(t => t.default);
        return {
            label: key,
            value: key,
            default: !!found
        };
    });

    // Check if any item is marked as default
    const hasDefault = defaultTypes.some(type => type.default);

    // If no default is found, set the first item's default to true
    if (!hasDefault && defaultTypes.length > 0) {
        defaultTypes[0].default = true;
        modelType.value = defaultTypes[0].label; // Set the modelType value to the label of the first element
    } else {
        // Find and set modelType to the label of the item that is marked as default
        const defaultItem = defaultTypes.find(type => type.default);
        modelType.value = defaultItem ? defaultItem.label : defaultTypes[0].label;
    }

    return defaultTypes;
};

const getContracts = async () => {
    const route = window.route('api.futures-options');
    const params = {
        root: modelCommodity.value.root
    };

    loading.value = true;

    axios.post(route, params)
        .then((response) => {
            if (props.initialSymbol && props.initialSymbol.contract) {
                contractData.value = setDefaultContract(response.data);
            } else {
                contractData.value = response.data;
            }
            types.value = setDefaultType(response.data);
        }).catch((error) => {
            console.error(error);
        }).then(() => {
            loading.value = false;
        })
};

watch(modelCommodity, (newCommodity, oldCommodity) => {
    if (newCommodity != oldCommodity) {
        if (newCommodity?.root && loading.value == false) {
            getContracts();
        }
    }
});

watch(modelContract, (newContract) => {
    if (!contracts.value || !newContract) return;

    // pass selected to parent
    emit('update:modelValue', modelContract.value);

    // const selectedItem = contractData.value[modelType.value][newContract];
    var selected = contracts.value.find(mContract => {
        return (mContract.value.symbol === modelContract.value.symbol || mContract.value.symbol === modelContract.value.underlyingFuture) && mContract.value.underlyingFuture === modelContract.value.underlyingFuture;
    });

    if (selected) {
        const label = selected.label;
        emit('update-description', `${modelType.value} - ${label}`);
    }
});
</script>
